import { useState } from 'react';
import { Plus, ShareNetwork, Copy, LockSimple, CheckCircle, Ticket, Receipt } from '@phosphor-icons/react';
import { getAsset, compareAmounts, type AssetCode, type ChequeDTO, type InvoiceDTO } from '@onyx/shared';
import { useCheques, useInvoices, useCheque, useInvoice, useMoneyMutation, useWallet } from '../lib/queries';
import { availableOf, useFiat } from '../lib/app';
import { useLang, useT, errorText } from '../lib/i18n';
import { cleanAmountInput, cryptoParts, displayDigits, formatCrypto, formatDateTime, formatFiat } from '../lib/format';
import { navigate, switchTab } from '../lib/router';
import { api, ApiError } from '../lib/api';
import { copyText, haptic, shareInline } from '../lib/telegram';
import { AssetChip, assetColor } from '../components/AssetChip';
import { Avatar, ErrorState, Facts, Field, Money, Notice, Row, Screen, ScreenHeader, Segmented, Skeleton, Slab, toast } from '../components/ui';
import { AssetSwitch } from './Send';

/* ---------- Cheque card: a gift card in the coin's colour ---------- */

function Cameo({ asset, amount, caption, foot }: { asset: AssetCode; amount: string; caption: React.ReactNode; foot?: React.ReactNode }) {
  const lang = useLang();
  const def = getAsset(asset)!;
  return (
    <div className="cameo" style={{ ['--coin' as string]: assetColor(asset) }}>
      <div className="cameo__top">
        <AssetChip asset={asset} size={36} />
        <span className="cameo__caption">{caption}</span>
      </div>
      <Money parts={cryptoParts(amount, displayDigits(Number(amount), def.stable), lang)} unit={asset} size="display" className="cameo__amount" />
      {foot && <div className="cameo__foot">{foot}</div>}
    </div>
  );
}

/* ---------- Payments tab ---------- */

export function Payments({ tab: initialTab }: { tab?: 'cheques' | 'invoices' }) {
  const t = useT();
  const lang = useLang();
  const [tab, setTab] = useState<'cheques' | 'invoices'>(initialTab ?? 'cheques');
  const cheques = useCheques();
  const invoices = useInvoices();

  return (
    <Screen className="payments">
      <ScreenHeader title={t['pay.title']} onBack={false} />
      <Segmented
        label={t['pay.title']}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'cheques', label: t['pay.cheques'] },
          { value: 'invoices', label: t['pay.invoices'] },
        ]}
      />
      {tab === 'cheques' ? (
        <>
          <Slab tone="quiet" icon={<Plus size={18} weight="bold" />} onClick={() => navigate('/pay/cheques/new')}>
            {t['pay.newCheque']}
          </Slab>
          {cheques.isPending ? (
            <Skeleton w="100%" h={64} r={14} />
          ) : cheques.isError ? (
            <ErrorState text={t['common.error']} onRetry={() => void cheques.refetch()} />
          ) : cheques.data.items.length === 0 ? (
            <div className="empty-explain">
              <Ticket size={28} aria-hidden="true" />
              <p>{t['pay.chequesEmpty']}</p>
            </div>
          ) : (
            <div className="rows">
              {cheques.data.items.map((c) => (
                <Row
                  key={c.id}
                  onClick={() => navigate(`/pay/cheque/${c.id}`)}
                  leading={<AssetChip asset={c.asset} size={40} />}
                  title={
                    <span className="num">
                      {formatCrypto(c.amountPer, 9, lang)} {c.asset}
                      {c.activations > 1 && ` × ${c.activations}`}
                    </span>
                  }
                  subtitle={
                    <>
                      {c.description ?? formatDateTime(c.createdAt, lang)}
                      {c.hasPassword && (
                        <>
                          {' · '}
                          <LockSimple size={12} weight="bold" className="inline-icon" aria-label={t['cheque.withPassword']} />
                        </>
                      )}
                    </>
                  }
                  trailing={<span className={`status status--cheque-${c.status}`}>{t[`cheque.status.${c.status}`]}</span>}
                  trailingSub={c.activations > 1 ? <span className="num">{c.activated}/{c.activations}</span> : undefined}
                  chevron
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <Slab tone="quiet" icon={<Plus size={18} weight="bold" />} onClick={() => navigate('/pay/invoices/new')}>
            {t['pay.newInvoice']}
          </Slab>
          {invoices.isPending ? (
            <Skeleton w="100%" h={64} r={14} />
          ) : invoices.isError ? (
            <ErrorState text={t['common.error']} onRetry={() => void invoices.refetch()} />
          ) : invoices.data.items.length === 0 ? (
            <div className="empty-explain">
              <Receipt size={28} aria-hidden="true" />
              <p>{t['pay.invoicesEmpty']}</p>
            </div>
          ) : (
            <div className="rows">
              {invoices.data.items.map((i) => (
                <Row
                  key={i.id}
                  onClick={() => navigate(`/pay/invoice/${i.id}`)}
                  leading={<AssetChip asset={i.asset} size={40} />}
                  title={
                    <span className="num">
                      {formatCrypto(i.amount, 9, lang)} {i.asset}
                    </span>
                  }
                  subtitle={i.description ?? formatDateTime(i.createdAt, lang)}
                  trailing={<span className={`status status--invoice-${i.status}`}>{t[`invoice.status.${i.status}`]}</span>}
                  trailingSub={i.payer ? (i.payer.username ? `@${i.payer.username}` : i.payer.firstName) : undefined}
                  chevron
                />
              ))}
            </div>
          )}
        </>
      )}
    </Screen>
  );
}

/* ---------- Create cheque ---------- */

export function ChequeCreate() {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const wallet = useWallet();
  const firstHeld = wallet.data?.balances.find((b) => Number(b.available) > 0)?.asset;
  const [asset, setAsset] = useState<AssetCode>(firstHeld ?? 'USDT');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'single' | 'multi'>('single');
  const [count, setCount] = useState('10');
  const [password, setPassword] = useState('');
  const [description, setDescription] = useState('');
  const def = getAsset(asset)!;
  const activations = kind === 'multi' ? Math.max(1, Math.min(10000, Number(count) || 0)) : 1;
  const total = amount ? (Number(amount) * activations).toFixed(def.decimals) : '0';
  const over = compareAmounts(total, availableOf(wallet.data, asset)) > 0;
  const price = wallet.data?.prices[asset]?.[fiat] ?? 0;
  const create = useMoneyMutation((v: { asset: string; amountPer: string; activations: number; password?: string; description?: string }) =>
    api<ChequeDTO>('/cheques', { body: v }),
  );

  return (
    <Screen
      footer={
        <Slab
          disabled={!amount || Number(amount) <= 0 || over || (kind === 'multi' && activations < 2)}
          loading={create.isPending}
          onClick={() =>
            create.mutate(
              { asset, amountPer: amount, activations, password: password.trim() || undefined, description: description.trim() || undefined },
              {
                onSuccess: (c) => {
                  haptic.success();
                  navigate(`/pay/cheque/${c.id}`, { replace: true });
                },
                onError: (e) => {
                  haptic.error();
                  toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined));
                },
              },
            )
          }
        >
          {t['cheque.create']}
        </Slab>
      }
    >
      <ScreenHeader title={t['cheque.title']} />
      <Segmented
        label={t['cheque.title']}
        value={kind}
        onChange={setKind}
        options={[
          { value: 'single', label: t['cheque.single'] },
          { value: 'multi', label: t['cheque.multi'] },
        ]}
      />
      <AssetSwitch asset={asset} onPick={(a) => { setAsset(a); setAmount(''); }} />
      <Field label={t['cheque.amount']} htmlFor="cq-amount" hint={<span className="num">≈ {formatFiat(Number(amount || 0) * price, fiat, lang)}</span>}>
        <div className="input-group">
          <input
            id="cq-amount"
            className="input input--amount num"
            inputMode="decimal"
            placeholder="0"
            value={lang === 'ru' ? amount.replace('.', ',') : amount}
            onChange={(e) => setAmount(cleanAmountInput(e.target.value, def.decimals))}
          />
          <span className="input-group__unit">{asset}</span>
        </div>
      </Field>
      {kind === 'multi' && (
        <Field label={t['cheque.activations']} htmlFor="cq-count">
          <input id="cq-count" className="input num" inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, '').slice(0, 5))} />
        </Field>
      )}
      <Field label={t['cheque.description']} htmlFor="cq-desc">
        <input id="cq-desc" className="input" maxLength={200} value={description} placeholder={t['cheque.descriptionPh']} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label={t['cheque.password']} htmlFor="cq-pass">
        <input id="cq-pass" className="input" maxLength={64} value={password} placeholder={t['cheque.passwordPh']} autoComplete="off" onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Facts items={[{ label: t['cheque.total'], value: <span className="num">{formatCrypto(total, def.decimals, lang)} {asset}</span>, strong: true }]} />
      {over && <Notice tone="bad">{t['err.insufficient_funds']}</Notice>}
    </Screen>
  );
}

/* ---------- Cheque detail (creator) and claim (recipient) ---------- */

export function ChequeView({ id }: { id: string }) {
  const t = useT();
  const lang = useLang();
  const q = useCheque(id);
  const [password, setPassword] = useState('');
  const [claimed, setClaimed] = useState(false);
  const claim = useMoneyMutation((v: { password?: string }) => api<{ asset: AssetCode; amount: string }>(`/cheques/${id}/claim`, { body: v }));
  const cancel = useMoneyMutation(() => api(`/cheques/${id}/cancel`, { body: {} }));
  const c = q.data;

  if (q.isPending) {
    return (
      <Screen>
        <ScreenHeader title={t['pay.cheques']} />
        <Skeleton w="100%" h={200} r={28} />
      </Screen>
    );
  }
  if (q.isError || !c) return <Screen><ScreenHeader title={t['pay.cheques']} /><ErrorState text={t['cheque.gone']} /></Screen>;

  const from = c.creator ? (c.creator.username ? `@${c.creator.username}` : c.creator.firstName) : '';
  const caption = c.activations > 1 ? t['cheque.multiShort'] : t[`cheque.status.${c.status}`];

  if (c.mine) {
    return (
      <Screen
        footer={
          c.status === 'active' ? (
            <>
              <Slab icon={<ShareNetwork size={18} weight="bold" />} onClick={() => shareInline(c.id, c.link)}>
                {t['cheque.share']}
              </Slab>
              <Slab tone="quiet" icon={<Copy size={18} />} onClick={() => void copyText(c.link).then(() => toast.good(t['deposit.copied']))}>
                {t['cheque.copyLink']}
              </Slab>
            </>
          ) : undefined
        }
      >
        <ScreenHeader title={t['pay.cheques']} />
        <Cameo asset={c.asset} amount={c.amountPer} caption={caption} foot={c.description} />
        <Facts
          items={[
            c.activations > 1 && { label: t['cheque.activations'], value: <span className="num">{c.activated} / {c.activations}</span> },
            c.hasPassword && { label: t['cheque.password'], value: <LockSimple size={16} weight="bold" aria-label={t['cheque.withPassword']} /> },
            { label: t['detail.date'], value: formatDateTime(c.createdAt, lang) },
            { label: t['detail.code'], value: <span className="num">{c.id}</span> },
          ]}
        />
        <Notice>{t['cheque.own']}</Notice>
        {c.status === 'active' && (
          <Slab
            tone="danger"
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate(undefined, {
                onSuccess: () => {
                  haptic.success();
                  void q.refetch();
                },
                onError: (e) => toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined)),
              })
            }
          >
            {t['cheque.cancel']}
          </Slab>
        )}
      </Screen>
    );
  }

  const gone = c.status !== 'active' && !c.claimedByMe && !claimed;
  const got = c.claimedByMe || claimed;

  return (
    <Screen
      className="claim"
      footer={
        got ? (
          <Slab onClick={() => switchTab('/')}>{t['common.done']}</Slab>
        ) : gone ? (
          <Slab tone="quiet" onClick={() => switchTab('/')}>{t['common.close']}</Slab>
        ) : (
          <Slab
            loading={claim.isPending}
            disabled={c.hasPassword && !password.trim()}
            onClick={() =>
              claim.mutate(
                { password: password.trim() || undefined },
                {
                  onSuccess: () => {
                    haptic.success();
                    setClaimed(true);
                  },
                  onError: (e) => {
                    haptic.error();
                    toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined));
                  },
                },
              )
            }
          >
            {t['cheque.claim']} {formatCrypto(c.amountPer, 9, lang)} {c.asset}
          </Slab>
        )
      }
    >
      <ScreenHeader title={`${t['cheque.from']} ${from}`} />
      <Cameo asset={c.asset} amount={c.amountPer} caption={got ? t['cheque.claimed'] : c.activations > 1 ? t['cheque.activated'](c.activated, c.activations) : t['pay.cheques']} foot={c.description} />
      {c.creator && (
        <div className="recipient">
          <Avatar user={c.creator} size={40} />
          <span className="recipient__text">
            <span className="recipient__name">{c.creator.firstName}</span>
            <span className="recipient__sub">{c.creator.username ? `@${c.creator.username}` : ''}</span>
          </span>
        </div>
      )}
      {got ? (
        <Notice tone="good" icon={<CheckCircle size={18} weight="fill" />}>
          {claimed ? t['cheque.claimedBody'] : t['cheque.alreadyClaimed']}
        </Notice>
      ) : gone ? (
        <Notice tone="warn">{t['cheque.gone']}</Notice>
      ) : (
        c.hasPassword && (
          <Field label={t['cheque.enterPassword']} htmlFor="claim-pass">
            <input id="claim-pass" className="input" value={password} autoComplete="off" onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )
      )}
    </Screen>
  );
}

/* ---------- Invoices ---------- */

const EXPIRY = [0, 1, 24, 72, 168];

export function InvoiceCreate() {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const wallet = useWallet();
  const [asset, setAsset] = useState<AssetCode>('USDT');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState(24);
  const def = getAsset(asset)!;
  const price = wallet.data?.prices[asset]?.[fiat] ?? 0;
  const create = useMoneyMutation((v: { asset: string; amount: string; description?: string; expiresInHours?: number }) => api<InvoiceDTO>('/invoices', { body: v }));

  return (
    <Screen
      footer={
        <Slab
          disabled={!amount || Number(amount) <= 0}
          loading={create.isPending}
          onClick={() =>
            create.mutate(
              { asset, amount, description: description.trim() || undefined, expiresInHours: hours || undefined },
              {
                onSuccess: (i) => {
                  haptic.success();
                  navigate(`/pay/invoice/${i.id}`, { replace: true });
                },
                onError: (e) => toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined)),
              },
            )
          }
        >
          {t['invoice.create']}
        </Slab>
      }
    >
      <ScreenHeader title={t['invoice.title']} />
      <AssetSwitch asset={asset} onPick={setAsset} onlyHeld={false} />
      <Field label={t['invoice.amount']} htmlFor="inv-amount" hint={<span className="num">≈ {formatFiat(Number(amount || 0) * price, fiat, lang)}</span>}>
        <div className="input-group">
          <input
            id="inv-amount"
            className="input input--amount num"
            inputMode="decimal"
            placeholder="0"
            value={lang === 'ru' ? amount.replace('.', ',') : amount}
            onChange={(e) => setAmount(cleanAmountInput(e.target.value, def.decimals))}
          />
          <span className="input-group__unit">{asset}</span>
        </div>
      </Field>
      <Field label={t['invoice.description']} htmlFor="inv-desc">
        <input id="inv-desc" className="input" maxLength={200} value={description} placeholder={t['invoice.descriptionPh']} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label={t['invoice.expires']}>
        <Segmented
          label={t['invoice.expires']}
          value={hours}
          onChange={setHours}
          options={EXPIRY.map((h) => ({ value: h, label: h ? t['invoice.hours'](h) : t['invoice.never'] }))}
        />
      </Field>
    </Screen>
  );
}

export function InvoiceView({ id }: { id: string }) {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const q = useInvoice(id);
  const wallet = useWallet();
  const [paid, setPaid] = useState(false);
  const pay = useMoneyMutation(() => api(`/invoices/${id}/pay`, { body: {} }));
  const cancel = useMoneyMutation(() => api(`/invoices/${id}/cancel`, { body: {} }));
  const i = q.data;

  if (q.isPending)
    return (
      <Screen>
        <ScreenHeader title={t['invoice.payTitle']} />
        <Skeleton w="100%" h={200} r={28} />
      </Screen>
    );
  if (q.isError || !i) return <Screen><ScreenHeader title={t['invoice.payTitle']} /><ErrorState text={t['invoice.gone']} /></Screen>;

  const creator = i.creator ? (i.creator.username ? `@${i.creator.username}` : i.creator.firstName) : '';
  const have = availableOf(wallet.data, i.asset);
  const short = compareAmounts(have, i.amount) < 0;
  const price = wallet.data?.prices[i.asset]?.[fiat] ?? 0;
  const status = paid ? 'paid' : i.status;

  if (i.mine) {
    return (
      <Screen
        footer={
          i.status === 'active' ? (
            <>
              <Slab icon={<ShareNetwork size={18} weight="bold" />} onClick={() => shareInline(i.id, i.link)}>
                {t['cheque.share']}
              </Slab>
              <Slab tone="quiet" icon={<Copy size={18} />} onClick={() => void copyText(i.link).then(() => toast.good(t['deposit.copied']))}>
                {t['cheque.copyLink']}
              </Slab>
            </>
          ) : undefined
        }
      >
        <ScreenHeader title={t['pay.invoices']} />
        <Cameo asset={i.asset} amount={i.amount} caption={t[`invoice.status.${i.status}`]} foot={i.description} />
        <Facts
          items={[
            i.payer && { label: t['invoice.paidBy'], value: i.payer.username ? `@${i.payer.username}` : i.payer.firstName },
            i.paidAt && { label: t['invoice.status.paid'], value: formatDateTime(i.paidAt, lang) },
            i.expiresAt && i.status === 'active' && { label: t['invoice.expires'], value: formatDateTime(i.expiresAt, lang) },
            { label: t['detail.code'], value: <span className="num">{i.id}</span> },
          ]}
        />
        {i.status === 'active' && (
          <>
            <Notice>{t['invoice.own']}</Notice>
            <Slab
              tone="danger"
              loading={cancel.isPending}
              onClick={() => cancel.mutate(undefined, { onSuccess: () => void q.refetch(), onError: (e) => toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined)) })}
            >
              {t['invoice.cancel']}
            </Slab>
          </>
        )}
      </Screen>
    );
  }

  return (
    <Screen
      className="claim"
      footer={
        status === 'active' ? (
          short ? (
            <Slab onClick={() => navigate(`/deposit/${i.asset}`)}>{t['home.deposit']} {i.asset}</Slab>
          ) : (
            <Slab
              loading={pay.isPending}
              onClick={() =>
                pay.mutate(undefined, {
                  onSuccess: () => {
                    haptic.success();
                    setPaid(true);
                  },
                  onError: (e) => {
                    haptic.error();
                    toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined));
                  },
                })
              }
            >
              {t['invoice.pay']} {formatCrypto(i.amount, 9, lang)} {i.asset}
            </Slab>
          )
        ) : (
          <Slab tone={paid ? 'primary' : 'quiet'} onClick={() => switchTab('/')}>
            {paid ? t['common.done'] : t['common.close']}
          </Slab>
        )
      }
    >
      <ScreenHeader title={t['invoice.payTitle']} />
      <Cameo asset={i.asset} amount={i.amount} caption={t[`invoice.status.${status}`]} foot={i.description} />
      <Facts
        items={[
          { label: t['invoice.from'], value: creator },
          { label: t['swap.value'], value: <span className="num">{formatFiat(Number(i.amount) * price, fiat, lang)}</span> },
          { label: t['invoice.youHave'], value: <span className={`num${short ? ' is-bad' : ''}`}>{formatCrypto(have, 9, lang)} {i.asset}</span> },
        ]}
      />
      {paid && (
        <Notice tone="good" icon={<CheckCircle size={18} weight="fill" />}>
          {t['invoice.paidTitle']}
        </Notice>
      )}
      {!paid && status !== 'active' && <Notice tone="warn">{t['invoice.gone']}</Notice>}
      {short && status === 'active' && <Notice tone="warn">{t['err.insufficient_funds']}</Notice>}
    </Screen>
  );
}
